import React, { useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import emailjs from "emailjs-com";

const PageHeader = styled.section`
  height: 300px;
  background: linear-gradient(rgba(220, 38, 38, 0.8), rgba(185, 28, 28, 0.8)), url("/images/get-a-quote.webp");
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  color: white;

  h1 {
    color: white;
    font-size: 3rem;
    text-align: center;
    width: 100%;
  }

  @media (max-width: 768px) {
    height: 200px;
    h1 {
      font-size: 2rem;
    }
  }
`;

const ContentSection = styled.section`
  padding: 80px 0;
  background: white;

  .content-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    align-items: start;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 2rem;
    }
  }
`;

const QuoteForm = styled.div`
  background: #f8f9fa;
  padding: 3rem;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);

  h2 {
    color: #dc2626;
    margin-bottom: 2rem;
    text-align: center;
  }

  form {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .form-group {
    display: flex;
    flex-direction: column;

    label {
      margin-bottom: 0.5rem;
      font-weight: 500;
      color: #333;
    }

    input,
    select,
    textarea {
      padding: 12px 15px;
      border: 2px solid #e9ecef;
      border-radius: 8px;
      font-size: 16px;
      transition: border-color 0.3s ease;
      font-family: inherit;

      &:focus {
        outline: none;
        border-color: #dc2626;
        box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
      }

      &.error {
        border-color: #e74c3c;
      }
    }

    textarea {
      resize: vertical;
      min-height: 120px;
    }

    .error-message {
      color: #e74c3c;
      font-size: 14px;
      margin-top: 0.5rem;
    }
  }

  .submit-btn {
    background: #dc2626;
    color: white;
    border: none;
    padding: 15px 30px;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    text-transform: uppercase;
    letter-spacing: 1px;

    &:hover:not(:disabled) {
      background: #b91c1c;
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba(220, 38, 38, 0.3);
    }

    &:disabled {
      background: #ccc;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  }
`;

const InfoSection = styled.div`
  .quote-image {
    width: 100%;
    height: 300px;
    object-fit: cover;
    border-radius: 10px;
    margin-bottom: 2rem;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }

  .info-card {
    background: #f8f9fa;
    padding: 2rem;
    border-radius: 10px;
    margin-bottom: 2rem;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);

    h3 {
      color: #dc2626;
      margin-bottom: 1rem;
    }

    ul {
      list-style: none;
      padding: 0;

      li {
        padding: 0.5rem 0;
        color: #666;
        position: relative;
        padding-left: 1.5rem;

        &:before {
          content: "✓";
          position: absolute;
          left: 0;
          color: #dc2626;
          font-weight: bold;
        }
      }
    }
  }

  .contact-card {
    background: #dc2626;
    color: white;
    padding: 2rem;
    border-radius: 10px;
    text-align: center;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);

    h3 {
      color: white;
      margin-bottom: 1rem;
    }

    .contact-item {
      margin: 1rem 0;

      a {
        color: white;
        font-weight: 500;
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }
    }
  }
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
  padding: 2rem;
  border-radius: 8px;
  text-align: center;

  h3 {
    color: #155724;
    margin-bottom: 1rem;
  }

  button {
    margin-top: 1rem;
  }
`;

const GetQuote = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    servicestype: "",
    details: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    }

    if (!formData.servicestype.trim()) {
      newErrors.servicestype = "Service type is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        to_email: "contact@ausgriptyres.com.au",
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        service_type: formData.servicestype,
        details: formData.details,
        subject: `Quote Request from ${formData.name}`,
      };

      const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
      const USER_ID = process.env.REACT_APP_EMAILJS_USER_ID || process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

      if (SERVICE_ID && TEMPLATE_ID && USER_ID) {
        await emailjs.send(SERVICE_ID, TEMPLATE_ID, payload, USER_ID);
      } else {
        console.log("EmailJS not configured — payload:", payload);
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      setIsSubmitted(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        servicestype: "",
        details: "",
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Failed to send quote request. Please try again or contact us directly.");
    }

    setIsSubmitting(false);
  };

  return (
    <>
      <PageHeader>
        <div className="container">
          <motion.h1
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}>
            Get A Quote
          </motion.h1>
        </div>
      </PageHeader>

      <ContentSection>
        <div className="container">
          <div className="content-grid">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}>
              <QuoteForm>
                {isSubmitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}>
                    <SuccessMessage>
                      <h3>Thank you for your quote request!</h3>
                      <p>We have received your request and will get back to you with a competitive quote within 24 hours.</p>
                      <p>Our team will review your requirements and provide you with the best possible pricing for your needs.</p>
                      <button
                        className="btn"
                        onClick={() => setIsSubmitted(false)}>
                        Request Another Quote
                      </button>
                    </SuccessMessage>
                  </motion.div>
                ) : (
                  <>
                    <h2>Request Your Quote</h2>
                    <form onSubmit={handleSubmit}>
                      <div className="form-group">
                        <label htmlFor="name">Name *</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className={errors.name ? "error" : ""}
                          placeholder="Enter your full name"
                        />
                        {errors.name && <span className="error-message">{errors.name}</span>}
                      </div>

                      <div className="form-group">
                        <label htmlFor="phone">Phone Number *</label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className={errors.phone ? "error" : ""}
                          placeholder="Enter your phone number"
                        />
                        {errors.phone && <span className="error-message">{errors.phone}</span>}
                      </div>

                      <div className="form-group">
                        <label htmlFor="email">E-mail *</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className={errors.email ? "error" : ""}
                          placeholder="Enter your email address"
                        />
                        {errors.email && <span className="error-message">{errors.email}</span>}
                      </div>

                      <div className="form-group">
                        <label htmlFor="servicestype">Type of Services *</label>
                        <select
                          id="servicestype"
                          name="servicestype"
                          value={formData.servicestype}
                          onChange={handleChange}
                          className={errors.servicestype ? "error" : ""}>
                          <option value="">-- Please Select Type of Services --</option>
                          <option value="Mobile Tyre Fitting">Mobile Tyre Fitting</option>
                          {/* Breakdown cover removed per request */}
                          <option value="Reliable New Tyres">Reliable New Tyres</option>
                          <option value="Tyre Puncture Repair">Tyre Puncture Repair</option>
                          <option value="Tyre Rotation">Tyre Rotation</option>
                          <option value="Trailer tyre repair">Trailer tyre repair services</option>
                          {/* Vikrant removed — only Landlux is offered */}
                          <option value="Landlux Tyres">Landlux Tyres</option>
                        </select>
                        {errors.servicestype && <span className="error-message">{errors.servicestype}</span>}
                      </div>

                      <div className="form-group">
                        <label htmlFor="details">Message</label>
                        <textarea
                          id="details"
                          name="details"
                          value={formData.details}
                          onChange={handleChange}
                          placeholder="Please provide additional details about your requirements..."
                          rows="4"
                        />
                      </div>

                      <button
                        type="submit"
                        className="submit-btn"
                        disabled={isSubmitting}>
                        {isSubmitting ? "Sending Request..." : "Submit Quote Request"}
                      </button>
                    </form>
                  </>
                )}
              </QuoteForm>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}>
              <InfoSection>
                <img
                  src="/images/get-a-quote.webp"
                  alt="Get a Quote"
                  className="quote-image"
                />

                <div className="info-card">
                  <h3>Why Get a Quote from Ausgrip Tyres?</h3>
                  <ul>
                    <li>Competitive pricing across all services</li>
                    <li>Transparent, no hidden costs</li>
                    <li>Professional assessment of your needs</li>
                    <li>Quality products from trusted brands</li>
                    <li>Expert advice and recommendations</li>
                    <li>Fast turnaround on quotes</li>
                    <li>Flexible payment options</li>
                    <li>Warranty on all work performed</li>
                  </ul>
                </div>

                <div className="contact-card">
                  <h3>Need Immediate Assistance?</h3>
                  <div className="contact-item">
                    <strong>Call us now:</strong>
                    <br />
                    <a href="tel:0413960242">0413960242</a>
                  </div>
                  <div className="contact-item">
                    <strong>Email us:</strong>
                    <br />
                    <a href="mailto:contact@ausgriptyres.com.au">contact@ausgriptyres.com.au</a>
                  </div>
                  <p>Our team will respond promptly to quote requests.</p>
                </div>
              </InfoSection>
            </motion.div>
          </div>
        </div>
      </ContentSection>
    </>
  );
};

export default GetQuote;
